import type { MouseEvent } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as ChevronIcon } from '../../../../../../assets/icons/chevron-down.svg';
import Badge from '../../../../../../shared/components/Globals/Badge/Badge';
import Table from '../../../../../../shared/components/Globals/Table/Table';
import { CLICK_ACTION, VIEW_CLIENT_PROFILE } from '../../../../../../shared/constants/tagManager';
import type { IClient, IClientRowProps, IClientsColumn } from '../../../../../../shared/interfaces/clients';
import TagManagerService from '../../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_CLIENTS } from '../../../../../../shared/translations/clients';
import { formatDateDisplay, parseDateISO } from '../../../../../../shared/utils/dates/dates';
import { EMPTY_FIELD } from '../../../../../../shared/utils/prospects/prospects';
import { toKebabCase } from '../../../../../../shared/utils/utils';

import './ClientRow.scss';

const BADGES = TRANSLATIONS_CLIENTS.clientsList.badges;

const EmptyCell = () => <span className="client-row__empty">{EMPTY_FIELD}</span>;

const ClientRow = ({ client, columns, onSelect }: IClientRowProps) => {
  const { t, i18n } = useTranslation();

  const renderCompany = (row: IClient) => (
    <div className="client-row__company">
      <span className="client-row__company-name">{row.companyName}</span>
      {row.hasActivationError && <Badge variant="lost">{t(BADGES.activationError)}</Badge>}
      {!row.hasActivationError && row.isNew && <Badge variant="success">{t(BADGES.new)}</Badge>}
    </div>
  );

  const renderLocation = (row: IClient) => {
    if (!row.location) return <EmptyCell />;
    return <span className="client-row__text">{row.location}</span>;
  };

  const renderEnrollmentWindow = (row: IClient) => {
    if (row.hasActivationError || !row.enrollmentWindow) return <EmptyCell />;
    return (
      <span className="client-row__enrollment-window">
        {formatDateDisplay(parseDateISO(row.enrollmentWindow), i18n.language)}
      </span>
    );
  };

  const renderCell = (column: IClientsColumn) => {
    switch (column.id) {
      case 'company':
        return renderCompany(client);
      case 'companySize':
        if (client.workforceSize !== null) {
          return <span className="client-row__text">{client.workforceSize.toLocaleString(i18n.language)}</span>;
        }
        return client.companySize ? <span className="client-row__text">{client.companySize}</span> : <EmptyCell />;
      case 'industry':
        if (client.activity) {
          return <span className="client-row__text">{client.activity}</span>;
        }
        return client.industry ? <span className="client-row__text">{client.industry}</span> : <EmptyCell />;
      case 'location':
        return renderLocation(client);
      case 'enrollmentWindow':
        return renderEnrollmentWindow(client);
      default:
        return null;
    }
  };

  const handleSelect = onSelect
    ? () => {
        TagManagerService.pushEvent(CLICK_ACTION, VIEW_CLIENT_PROFILE);
        onSelect(client);
      }
    : undefined;

  const handleActionClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleSelect?.();
  };

  return (
    <Table.Row interactive={!!handleSelect} className="client-row" onClick={handleSelect}>
      {columns.map((column) => (
        <Table.Cell key={column.id} className={`clients-table__col--${toKebabCase(column.id)}`}>
          {renderCell(column)}
        </Table.Cell>
      ))}
      <Table.Cell className="clients-table__col--action">
        {handleSelect && (
          <button
            type="button"
            className="client-row__action"
            onClick={handleActionClick}
            aria-label={t(TRANSLATIONS_CLIENTS.clientsList.rowAction, { companyName: client.companyName })}
          >
            <ChevronIcon className="client-row__chevron" aria-hidden="true" />
          </button>
        )}
      </Table.Cell>
    </Table.Row>
  );
};

export default ClientRow;
